import { scrubStr } from '@atlaskit/adf-utils/scrub';
import {
	ACTION,
	ACTION_SUBJECT,
	EVENT_TYPE,
	ACTION_SUBJECT_ID,
} from '@atlaskit/editor-common/analytics';
import type { Node } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags';

import type { API } from '../../aiStreamingOrchestratorPluginType';

export function findNodeByLocalIdAndDoc(
	doc: Node,
	localId: string,
): { node: Node; parent: Node | null; pos: number } | undefined {
	let nodeResult: { node: Node; parent: Node | null; pos: number } | undefined;
	doc.descendants((node, pos, parent) => {
		if (nodeResult) {
			return false;
		}
		if (node.attrs.localId === localId) {
			nodeResult = { node, pos, parent };
		}
		return !nodeResult;
	});
	return nodeResult;
}

/**
 * This find will search through the entire document and only return the last breadth-first node match rather then the first.
 * This is NOT a depth-first search.
 * @param doc
 * @param localId
 * @returns
 */
export function findLastNodeByLocalIdAndDoc(
	doc: Node,
	localId: string,
): { node: Node; parent: Node | null; pos: number } | undefined {
	let nodeResult: { node: Node; parent: Node | null; pos: number } | undefined;
	doc.descendants((node, pos, parent) => {
		if (node.attrs.localId === localId) {
			nodeResult = { node, pos, parent };
			return false;
		}
		return true;
	});
	return nodeResult;
}

export function findNodeByLocalIdAndApi(
	api: API | undefined,
	localId: string | false,
): Node | undefined {
	if (!localId) {
		return undefined;
	}
	let foundNode: Node | undefined;
	api?.core.actions.execute(({ tr }) => {
		foundNode = findNodeByLocalIdAndDoc(tr.doc, localId)?.node;
		return null;
	});
	return foundNode;
}

/**
 * Finds a node by its localId and calls the callback with the node, position, and parent.
 * @param tr - The transform to search in.
 * @param localId - The localId of the node to find.
 * @param callback - The callback to call with the node, position, and parent.
 * @returns The transform.
 */
export function findNodeByLocalId(
	api: API | undefined,
	tr: Transaction,
	localId: string,
	callback: ({ node, pos, parent }: { node: Node; parent: Node | null; pos: number }) => void,
	reversed?: boolean,
): Transaction | null {
	const result = (
		fg('platform_editor_ai_aifc_patch_ga_blockers')
			? reversed
				? findLastNodeByLocalIdAndDoc
				: findNodeByLocalIdAndDoc
			: findNodeByLocalIdAndDoc
	)(tr.doc, localId);

	if (result) {
		api?.aiExperience?.actions.tagAsAIStreamingTransformation(tr);
		callback(result);
	}
	if (!result) {
		const localIdState = api?.localId?.sharedState.currentState();

		let localIdStatus: string;
		if (!localIdState) {
			localIdStatus = 'localIdPluginMissing';
		} else if (!localIdState.localIdWatchmenEnabled) {
			localIdStatus = 'localIdWatchmenDisabled';
		} else {
			localIdStatus = localIdState.localIdStatus?.get(localId) ?? 'notFound';
		}

		let docSize: number | undefined;
		// Quick and dirty way to get the node size - do not copy this pattern, use `requestDocument`
		api?.core.actions.execute(({ tr }) => {
			docSize = tr.doc.nodeSize;
			return null;
		});
		api?.analytics?.actions.fireAnalyticsEvent({
			action: ACTION.LOCAL_ID_NOT_FOUND,
			actionSubject: ACTION_SUBJECT.AI_STREAMING,
			actionSubjectId: ACTION_SUBJECT_ID.EXPERIENCE_APPLICATION,
			eventType: EVENT_TYPE.OPERATIONAL,
			attributes: {
				localIdLength: localId?.length,
				docSize,
				scrubbedLocalId: fg('platform_editor_ai_traceid_in_analytics_events')
					? scrubStr(localId)
					: '',
				localIdStatus: fg('platform_editor_ai_aifc_localid_error_reporting')
					? localIdStatus
					: 'unavailable',
				localIdStatusSize: localIdState?.localIdStatus?.size ?? 0,
			},
		});
		if (fg('platform_editor_ai_aifc_patch_ga')) {
			return null;
		} else {
			throw new Error(`Node with localId ${localId} not found`);
		}
	}
	return tr;
}
